import React from "react";
import { styled } from "@linaria/react";
import t from "../../i18n";
import type { Project } from "../../api/useProjects";
import { canUserDoOnProject } from "diagnostics-domain/canUserDo";
import type { User } from "../../api/useUser";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine, FileCopyLine, PencilLine } from "@ui/IconV2/react";

interface P {
  user: User;
  className?: string;
  project: Project;
  openEditModal: (e: React.MouseEvent) => void;
  openDeleteModal: (e: React.MouseEvent) => void;
  openDuplicateModal: (e: React.MouseEvent) => void;
}

const ActionIcons = styled.div`
  display: flex;
  align-items: center;
`;

const Actions = ({
  user,
  className,
  project,
  openEditModal,
  openDuplicateModal,
  openDeleteModal,
}: P) => {
  const hasEditProjectPermission = canUserDoOnProject(user, "PROJECT_EDIT", project.id);
  const hasDeleteProjectPermission = canUserDoOnProject(user, "PROJECT_MANAGE", project.id);
  const hasDuplicationProjectPermission = canUserDoOnProject(user, "PROJECT_DUPLICATE", project.id);

  const isProdProject = !project.draft;
  return (
    <ActionIcons className={className} data-cy="project-action-icons">
      {hasEditProjectPermission && (
        <ButtonIconV2
          title={t("projects.actions.edit")}
          data-cy="edit-project"
          onClick={openEditModal}
        >
          <PencilLine size="m" />
        </ButtonIconV2>
      )}
      {hasDuplicationProjectPermission && (
        <ButtonIconV2
          title={t("projects.actions.duplicate")}
          data-cy="duplicate-project"
          onClick={openDuplicateModal}
        >
          <FileCopyLine size="m" />
        </ButtonIconV2>
      )}
      {hasDeleteProjectPermission && (
        <ButtonIconV2
          title={
            project.draft
              ? t("projects.actions.delete")
              : t("projects.modal.delete.prod.project.tooltip")
          }
          data-cy="delete-project"
          onClick={openDeleteModal}
          disabled={isProdProject}
        >
          <DeleteBinLine size="m" />
        </ButtonIconV2>
      )}
    </ActionIcons>
  );
};

export default React.memo(Actions);
